import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';

// Shared styles and components for Settings panels

/* ---- 全局动效样式 ---- */
const PAGE_CSS = `
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes slideDown {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes slideInRight {
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
.animate-fade-in-up {
  animation: fadeInUp 0.25s ease-out;
}
.animate-slide-down {
  animation: slideDown 0.2s ease-out;
}
.animate-slide-in-right {
  animation: slideInRight 0.3s ease-out;
}
.animate-pulse {
  animation: pulse 2s ease-in-out infinite;
}
`;

if (typeof document !== 'undefined' && !document.getElementById('settings-animations')) {
  const style = document.createElement('style');
  style.id = 'settings-animations';
  style.textContent = PAGE_CSS;
  document.head.appendChild(style);
}

/* ---- 动画 hook ---- */
export function useAnimation() {
  useEffect(() => {
    return () => {
      const style = document.getElementById('settings-animations');
      if (style) style.remove();
    };
  }, []);
}

export const inputCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 bg-white text-gray-800 placeholder:text-gray-400 transition-colors';

export const labelCls =
  'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';

export const selectCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 bg-white text-gray-800 transition-colors cursor-pointer';

/* ---- Toggle 开关 ---- */
export function Toggle({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: () => void;
  /** 请求在途或被服务端禁止操作时置灰：不给「点了没反应」的假象 */
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      } ${checked ? 'bg-teal-500' : 'bg-gray-200'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

/* ---- 分区标题 ---- */
export function Section({ title }: { title: string }) {
  return (
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-2 pb-1 border-b border-gray-100 mb-4">
      {title}
    </p>
  );
}

/* ---- 行项目（带 toggle）---- */
export function ToggleRow({
  icon,
  title,
  desc,
  checked,
  onChange,
}: {
  icon: string;
  title: string;
  desc: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg mt-0.5">
          <i className={`${icon} text-gray-400 text-base`} />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800">{title}</p>
          <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
        </div>
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

/* ---- 保存成功提示 ---- */
export function SavedTip() {
  return (
    <div className="flex items-center gap-2 p-3 bg-teal-50 border border-teal-100 rounded-lg text-sm text-teal-600">
      <i className="ri-checkbox-circle-line" />
      设置已保存！
    </div>
  );
}

/* ---- 可收折分区（移动端手风琴 / 桌面端保持原样）---- */
export function CollapsibleSection({
  title,
  children,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleClick = () => {
    if (isControlled) {
      onOpenChange?.(!open);
    } else {
      setInternalOpen(o => !o);
    }
  };

  return (
    <div>
      {/* Header */}
      <button
        type="button"
        onClick={handleClick}
        className={`w-full flex items-center justify-between cursor-pointer transition-colors px-4 py-3.5
          ${open
            ? 'bg-gray-50 rounded-t-xl border border-b-0 border-gray-100'
            : 'bg-gray-50/70 rounded-xl border border-gray-100 hover:bg-gray-100/60'
          }
          md:px-0 md:py-0 md:pt-2 md:pb-1 md:bg-transparent md:border-0 md:border-b md:border-gray-100 md:rounded-none md:pointer-events-none md:cursor-default`}
      >
        <p className="text-xs font-semibold text-gray-500 md:text-gray-400 uppercase tracking-wider text-left">
          {title}
        </p>
        <div className="w-5 h-5 flex items-center justify-center md:hidden shrink-0">
          <i
            className={`ri-arrow-down-s-line text-gray-400 text-base transition-transform duration-200 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>

      {/* Content */}
      <div
        className={`${
          open
            ? 'block px-4 pb-4 pt-3 border border-t-0 border-gray-100 rounded-b-xl'
            : 'hidden'
        } md:!block md:px-0 md:pb-0 md:pt-4 md:border-0 md:rounded-none`}
      >
        {children}
      </div>
    </div>
  );
}
